/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject, zip} from 'rxjs';
import {map} from 'rxjs/operators';
import {UntypedFormGroup} from '@angular/forms';
import {Params} from '@angular/router';
import {CompoApiService, ApiResponse} from 'compob2b-libs';
import {ResetPasswordData} from '../interfaces/ResetPasswordData';
import {PasswordPair} from '../interfaces/PasswordPair';

@Injectable({
  providedIn: 'root',
})
export class ResetService {
  private readonly validPassFields: Subject<boolean> = new Subject();
  private readonly showForm$ = new BehaviorSubject<boolean>(false);

  /** Показывать ли форму смены пароля (true после успешной валидации токена). */
  readonly showForm: Observable<boolean> = this.showForm$.asObservable();

  constructor(private readonly apiService: CompoApiService) {
  }

  /**
   * Checkout reactive style of checking passwords
   * Combine two observables in one stream by zip operator and doing a job
   * in checkForm method
   *
   * isValidForm property doesn't need a subscription because it's being processed in template with async pipe
   * {@link https://rxjs-dev.firebaseapp.com/api/index/function/zip}
   */
  public validate(form: UntypedFormGroup): Observable<boolean> {
    const isValid = form.statusChanges;
    const isEquals = form.valueChanges;

    return zip(isValid, isEquals).pipe(
      map(([status, fields]) => {
        return this.checkForm(
          status,
          fields.password === fields.repeatPassword
        );
      })
    );
  }

  public passFields(): Subject<boolean> {
    return this.validPassFields;
  }

  private checkForm(status: string, fieldsAreIdentical: boolean): boolean {
    this.validPassFields.next(fieldsAreIdentical);
    return status === 'VALID' && fieldsAreIdentical;
  }

  public validateToken(params: Params): Observable<ApiResponse<string>> {
    return this.apiService.post<Params, string>('/validate-reset-token/', params);
  }

  public tokenValidatedSuccessfully(): void {
    this.showForm$.next(true);
  }

  public resetPassword(myObject: PasswordPair, params: Params): Observable<ApiResponse<string>> {
    const resetData: ResetPasswordData = {
      email: params.email,
      token: params.token,
      password: myObject.password,
      repeatPassword: myObject.repeatPassword,
    };

    return this.apiService.patch<ResetPasswordData, string>('/reset-password/', resetData);
  }
}
